import { useState } from 'react';
import { BarChart3, Eye, Download, Globe, RefreshCw, Lock } from 'lucide-react';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RANGES = [7, 30, 90];

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-[#1E2328] rounded-2xl p-6 border border-white/10">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg bg-[#137dc5]/15 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#3aa0e0]" />
      </div>
      <span className="text-slate-400 text-sm">{label}</span>
    </div>
    <div className="text-3xl font-bold text-white">{value}</div>
    {sub && <div className="text-slate-500 text-sm mt-1">{sub}</div>}
  </div>
);

const RankTable = ({ title, rows, emptyLabel }) => (
  <div className="bg-[#1E2328] rounded-2xl p-6 border border-white/10">
    <h3 className="text-white font-semibold mb-4">{title}</h3>
    {rows && rows.length ? (
      <ul className="space-y-3">
        {rows.map((r) => (
          <li key={r.name} className="flex items-center justify-between gap-4">
            <span className="text-slate-300 truncate">{r.name}</span>
            <span className="text-[#3aa0e0] font-semibold flex-shrink-0">{r.count}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-slate-500 text-sm">{emptyLabel}</p>
    )}
  </div>
);

const AnalyticsDashboard = () => {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = async (pw = password, range = days) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/analytics/summary?token=${encodeURIComponent(pw)}&days=${range}`);
      if (res.status === 401) {
        setError('Incorrect password');
        setData(null);
        setAuthed(false);
      } else if (!res.ok) {
        setError('Could not load analytics. Please try again.');
      } else {
        setData(await res.json());
        setAuthed(true);
      }
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    load(password, days);
  };

  const changeRange = (range) => {
    setDays(range);
    load(password, range);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center px-4">
        <Seo path="/analytics" title="Analytics | FlyFinder" description="FlyFinder private analytics dashboard." />
        <form onSubmit={handleLogin} className="bg-[#1E2328] rounded-2xl p-8 border border-white/10 w-full max-w-sm">
          <div className="w-12 h-12 rounded-xl bg-[#137dc5]/15 flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-[#3aa0e0]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Analytics</h1>
          <p className="text-slate-400 text-sm mb-6">Enter your dashboard password to continue.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-3 rounded-lg bg-[#0F1419] border border-white/15 text-white placeholder-slate-500 focus:ring-2 focus:ring-[#137dc5] focus:border-transparent mb-4"
            autoFocus
          />
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="w-full bg-[#137dc5] hover:bg-[#0f6aa8] text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            {loading ? 'Checking...' : 'View Analytics'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1419] py-12">
      <Seo path="/analytics" title="Analytics | FlyFinder" description="FlyFinder private analytics dashboard." />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-[#3aa0e0]" />
            <h1 className="text-3xl font-bold text-white">FlyFinder Analytics</h1>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => changeRange(r)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === r ? 'bg-[#137dc5] text-white' : 'bg-[#1E2328] text-slate-300 hover:bg-white/10 border border-white/10'
                }`}
              >
                {r}d
              </button>
            ))}
            <button
              onClick={() => load(password, days)}
              className="p-2 rounded-lg bg-[#1E2328] text-slate-300 hover:bg-white/10 border border-white/10"
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {data && (
          <>
            <p className="text-slate-500 text-sm mb-6">Last {data.range_days} days</p>
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <StatCard icon={Eye} label="Page Views" value={data.total_pageviews} />
              <StatCard icon={Download} label="App Store Clicks" value={data.total_downloads} />
              <StatCard icon={Globe} label="Click-through Rate" value={`${data.download_conversion_pct}%`} sub="downloads / page views" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <RankTable title="Top Traffic Sources" rows={data.top_sources} emptyLabel="No data yet" />
              <RankTable title="Top Pages" rows={data.top_pages} emptyLabel="No data yet" />
              <RankTable title="Download Clicks by Page" rows={data.download_pages} emptyLabel="No downloads yet" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
